import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const BASE_URL      = "https://senutour.com/api";
const CLOUD_NAME    = "dhs2cu6vv";
const UPLOAD_PRESET = "senutour_unsigned";

// Catégorie de service fixe pour ce dashboard — ne pas confondre avec
// le style du véhicule (Berline, SUV...) qui va dans typeVehicule.
const CATEGORIE_SERVICE = "Chauffeur";

const getToken = () => localStorage.getItem("token");
const getUser  = () => JSON.parse(localStorage.getItem("user") || "{}");

// ── Icônes SVG ───────────────────────────────────────────────
const IcGrid  = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>;
const IcCar   = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 17h14a2 2 0 0 0 2-2v-2.76a2 2 0 0 0-.5-1.32L18 6H6l-2.5 4.92A2 2 0 0 0 3 12.24V15a2 2 0 0 0 2 2z"/><circle cx="7.5" cy="17" r="1.5"/><circle cx="16.5" cy="17" r="1.5"/></svg>;
const IcEdit  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IcTrash = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>;
const IcUpload= () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>;
const IcX     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IcUser  = () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;

// ── Constantes ──────────────────────────────────────────────
const TYPES_VEHICULE = ["Berline", "SUV", "Minibus", "4x4", "Citadine", "Utilitaire"];
const VILLES         = ["Dakar", "Diamniadio", "Saly", "Saint-Louis", "Ziguinchor", "Thiès", "Touba", "Kaolack"];
const EQUIPEMENTS_LISTE = ["Climatisation", "GPS", "Bluetooth", "Sièges enfant", "Chauffeur inclus", "Carburant inclus", "Coffre spacieux", "4 portes"];

const VEHICULE_VIDE = {
  nom: "", typeVehicule: "Berline", marque: "", modele: "", annee: "",
  villeDepart: "Dakar", prix: "", prixJournee: "", capacite: "",
  telephone: "", whatsapp: "", nomProprietaire: "", description: "",
  equipements: [], disponible: true,
};

const formatPrix = (p) => p ? Number(p).toLocaleString("fr-FR") + " F" : "—";

const ONGLETS = [
  { id: "dashboard",  label: "Vue générale",   Icon: IcGrid },
  { id: "vehicules",  label: "Mes véhicules",  Icon: IcCar },
];

export default function ChauffeurDashboard() {
  const navigate    = useNavigate();
  const user        = getUser();
  const fileRef     = useRef();

  const [onglet, setOnglet]       = useState("dashboard");
  const [vehicules, setVehicules] = useState([]);
  const [loading, setLoading]     = useState(true);

  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(VEHICULE_VIDE);
  const [photos, setPhotos]       = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [editId, setEditId]       = useState(null);

  // ── Chargement des véhicules ─────────────────────────────
  useEffect(() => {
    const charger = async () => {
      try {
        const res = await fetch(`${BASE_URL}/transports/mes-vehicules`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        setVehicules(Array.isArray(data) ? data : []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    charger();
  }, []);

  // ── Upload photos vers Cloudinary ────────────────────────
  const uploadPhotos = async (files) => {
    setUploading(true);
    try {
      const urls = await Promise.all(files.map(async (file) => {
        const fd = new FormData();
        fd.append("file", file); fd.append("upload_preset", UPLOAD_PRESET);
        const r = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, { method: "POST", body: fd });
        const d = await r.json(); return d.secure_url;
      }));
      setPhotos(prev => [...prev, ...urls]);
    } catch { alert("Erreur upload."); }
    finally { setUploading(false); }
  };

  const toggleEquip = (e) => setForm(prev => ({
    ...prev,
    equipements: prev.equipements.includes(e) ? prev.equipements.filter(x => x !== e) : [...prev.equipements, e],
  }));

  // ── Sauvegarder véhicule ──────────────────────────────────
  const sauvegarderVehicule = async () => {
    if (!form.nom || !form.prix || !form.telephone) { alert("Nom, prix et téléphone sont obligatoires."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        type: CATEGORIE_SERVICE, // ── catégorie de service, fixe pour ce dashboard
        prix: parseInt(form.prix) || 0,
        prixJournee: parseInt(form.prixJournee) || parseInt(form.prix) || 0,
        capacite: parseInt(form.capacite) || 4,
        annee: parseInt(form.annee) || null,
        images: photos,
      };
      let res;
      if (editId) {
        res = await fetch(`${BASE_URL}/transports/${editId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${BASE_URL}/transports`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify(payload),
        });
      }
      const data = await res.json();
      if (res.ok) {
        if (editId) {
          setVehicules(prev => prev.map(v => v.id === editId ? data.transport : v));
        } else {
          setVehicules(prev => [data.transport, ...prev]);
        }
        setShowForm(false); setForm(VEHICULE_VIDE); setPhotos([]); setEditId(null);
      } else alert(data.message || "Erreur.");
    } catch { alert("Erreur serveur."); }
    finally { setSaving(false); }
  };

  const ouvrirEdit = (v) => {
    setEditId(v.id);
    setForm({
      nom: v.nom || "",
      // Repli sur v.type pour les anciens enregistrements créés avant la correction du bug
      typeVehicule: v.typeVehicule || v.type || "Berline",
      marque: v.marque || "", modele: v.modele || "",
      annee: v.annee || "", villeDepart: v.villeDepart || "Dakar", prix: v.prix || "",
      prixJournee: v.prixJournee || "", capacite: v.capacite || "", telephone: v.telephone || "",
      whatsapp: v.whatsapp || "", nomProprietaire: v.nomProprietaire || "", description: v.description || "",
      equipements: Array.isArray(v.equipements) ? v.equipements : [], disponible: v.disponible ?? true,
    });
    setPhotos(Array.isArray(v.images) ? v.images : []);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const supprimerVehicule = async (id) => {
    if (!window.confirm("Supprimer ce véhicule ?")) return;
    try {
      await fetch(`${BASE_URL}/transports/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setVehicules(prev => prev.filter(v => v.id !== id));
    } catch (e) { console.error(e); }
  };

  const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, color: "#7A5A3A", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 };
  const inputStyle = { width: "100%", boxSizing: "border-box", padding: "11px 14px", border: "1.5px solid #E8DCC8", borderRadius: 10, fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, outline: "none", color: "#1C1008", background: "white" };

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#FAF5EC", fontFamily: "'Space Grotesk',sans-serif", color: "#7A5A3A" }}>
      Chargement…
    </div>
  );

  return (
    <>
      <Navbar />
      <div style={{ fontFamily: "'Space Grotesk',sans-serif", background: "#FAF5EC", minHeight: "100vh", color: "#1C1008" }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,700;0,9..144,800;1,9..144,400&family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
          .onglet-btn { transition: all .2s; cursor: pointer; border: none; background: none; }
          .onglet-btn:hover { color: #C4441A !important; }
          .btn-primary { background: #C4441A; transition: background .2s; border: none; cursor: pointer; }
          .btn-primary:hover { background: #A03614; }
          .card-hover { transition: all .2s; }
          .card-hover:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(28,16,8,0.10) !important; }
          .pill { cursor: pointer; transition: all .15s; user-select: none; }
          .photo-zone { border: 2px dashed #E8DCC8; border-radius: 12px; padding: 28px; text-align: center; cursor: pointer; transition: border-color .2s; }
          .photo-zone:hover { border-color: #C4441A; }
          .vehicule-card { transition: all .2s; }
          .vehicule-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(28,16,8,0.10) !important; }
          @media(max-width:767px) { .stats-grid { grid-template-columns: 1fr 1fr !important; } .veh-grid { grid-template-columns: 1fr !important; } }
        `}</style>

        {/* ── HEADER ── */}
        <div style={{ background: "linear-gradient(135deg,#1C1008 0%,#2D1A08 100%)", padding: "48px 5% 36px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle,rgba(196,68,26,.07) 1px,transparent 1px)", backgroundSize: "28px 28px" }} />
          <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                <div style={{ width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg,#C4441A,#E8834A)", display: "flex", alignItems: "center", justifyContent: "center", border: "3px solid rgba(255,255,255,0.2)", flexShrink: 0, overflow: "hidden" }}>
                  {localStorage.getItem("sunutour_photo")
                    ? <img src={localStorage.getItem("sunutour_photo")} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="profil" />
                    : <IcUser />}
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#C4441A", textTransform: "uppercase", letterSpacing: 2, marginBottom: 4 }}>Dashboard Chauffeur · JOJ 2026</div>
                  <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 800, color: "white", margin: 0 }}>
                    {`${user.prenom || ""} ${user.nom || ""}`.trim() || "Mon espace chauffeur"}
                  </h1>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 3 }}>
                    {vehicules.length} véhicule{vehicules.length !== 1 ? "s" : ""} en location
                  </div>
                </div>
              </div>
              <button className="btn-primary" onClick={() => { setOnglet("vehicules"); setShowForm(true); setForm(VEHICULE_VIDE); setPhotos([]); setEditId(null); }}
                style={{ color: "white", borderRadius: 10, padding: "12px 22px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, boxShadow: "0 8px 20px rgba(196,68,26,0.35)" }}>
                + Ajouter un véhicule
              </button>
            </div>
          </div>
        </div>

        {/* ── ONGLETS ── */}
        <div style={{ background: "white", borderBottom: "1.5px solid #E8DCC8", padding: "0 5%", overflowX: "auto" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", gap: 4, minWidth: "max-content" }}>
            {ONGLETS.map(o => (
              <button key={o.id} className="onglet-btn" onClick={() => setOnglet(o.id)}
                style={{ padding: "16px 18px", fontSize: 13, fontWeight: 600, color: onglet === o.id ? "#C4441A" : "#7A5A3A", borderBottom: onglet === o.id ? "2px solid #C4441A" : "2px solid transparent", marginBottom: -1, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                <o.Icon /> {o.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "36px 5% 60px" }}>

          {/* ══ VUE GÉNÉRALE ══ */}
          {onglet === "dashboard" && (
            <>
              {vehicules.length === 0 && (
                <div style={{ background: "rgba(196,132,26,0.06)", border: "1.5px solid rgba(196,132,26,0.25)", borderRadius: 12, padding: "20px 24px", marginBottom: 28, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#C4841A", marginBottom: 4 }}>Aucun véhicule enregistré</div>
                    <div style={{ fontSize: 13, color: "#7A5A3A" }}>Ajoutez votre première voiture pour être visible sur la plateforme.</div>
                  </div>
                  <button className="btn-primary" onClick={() => { setOnglet("vehicules"); setShowForm(true); }}
                    style={{ color: "white", borderRadius: 8, padding: "10px 20px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>
                    Ajouter un véhicule →
                  </button>
                </div>
              )}

              <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 32 }}>
                {[
                  { label: "Véhicules",      value: vehicules.length,                                              color: "#C4441A" },
                  { label: "Disponibles",    value: vehicules.filter(v => v.disponible).length,                    color: "#3D6B2E" },
                  { label: "Indisponibles",  value: vehicules.filter(v => !v.disponible).length,                   color: "#C4841A" },
                  { label: "Note moyenne",   value: vehicules.length ? (vehicules.reduce((s,v) => s + (v.note||0), 0) / vehicules.length).toFixed(1) + "/5" : "—", color: "#C4441A" },
                ].map((s, i) => (
                  <div key={i} className="card-hover" style={{ background: "white", borderRadius: 14, padding: "22px", border: "1.5px solid #E8DCC8", boxShadow: "0 2px 12px rgba(28,16,8,0.06)" }}>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: "#7A5A3A", marginTop: 6, fontWeight: 600 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ background: "white", borderRadius: 14, border: "1.5px solid #E8DCC8", overflow: "hidden" }}>
                <div style={{ padding: "18px 24px", borderBottom: "1.5px solid #E8DCC8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 16, fontWeight: 800, color: "#1C1008", margin: 0 }}>Mes véhicules</h3>
                  <button onClick={() => setOnglet("vehicules")} style={{ fontSize: 12, color: "#C4441A", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>Voir tout →</button>
                </div>
                {vehicules.length === 0
                  ? <div style={{ padding: 32, textAlign: "center", color: "#7A5A3A", fontSize: 13 }}>Aucun véhicule pour l'instant</div>
                  : vehicules.slice(0, 4).map((v, i, arr) => (
                    <div key={v.id} style={{ padding: "13px 24px", borderBottom: i < arr.length - 1 ? "1px solid #F2E4C8" : "none", display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 44, height: 36, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: "#F2E4C8", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {v.images?.[0] ? <img src={v.images[0]} alt={v.nom} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <IcCar />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1C1008", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.nom}</div>
                        <div style={{ fontSize: 11, color: "#7A5A3A" }}>{v.typeVehicule || v.type} · {v.villeDepart}</div>
                      </div>
                      <div style={{ fontFamily: "'Fraunces',serif", fontSize: 14, fontWeight: 700, color: "#C4441A" }}>{formatPrix(v.prix)}<span style={{ fontSize: 11, fontWeight: 500 }}>/jour</span></div>
                    </div>
                  ))
                }
              </div>
            </>
          )}

          {/* ══ MES VÉHICULES ══ */}
          {onglet === "vehicules" && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
                <div>
                  <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 24, fontWeight: 800, color: "#1C1008", margin: 0 }}>Mes véhicules</h2>
                  <p style={{ fontSize: 13, color: "#7A5A3A", margin: "4px 0 0" }}>{vehicules.length} véhicule{vehicules.length !== 1 ? "s" : ""} enregistré{vehicules.length !== 1 ? "s" : ""}</p>
                </div>
                <button className="btn-primary" onClick={() => { setShowForm(!showForm); setForm(VEHICULE_VIDE); setPhotos([]); setEditId(null); }}
                  style={{ color: "white", borderRadius: 10, padding: "10px 20px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 700 }}>
                  {showForm && editId === null ? "Annuler" : "+ Ajouter un véhicule"}
                </button>
              </div>

              {showForm && (
                <div style={{ background: "white", borderRadius: 16, border: "1.5px solid #E8DCC8", padding: "32px", marginBottom: 28, boxShadow: "0 4px 20px rgba(28,16,8,0.08)" }}>
                  <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 20, fontWeight: 800, color: "#1C1008", marginBottom: 24 }}>
                    {editId ? "Modifier le véhicule" : "Nouveau véhicule"}
                  </h3>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Nom de l'annonce *</label>
                      <input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} placeholder="Ex: Toyota Corolla 2022 climatisée" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Type de véhicule</label>
                      <select value={form.typeVehicule} onChange={e => setForm({ ...form, typeVehicule: e.target.value })} style={inputStyle}>
                        {TYPES_VEHICULE.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Marque</label>
                      <input value={form.marque} onChange={e => setForm({ ...form, marque: e.target.value })} placeholder="Ex: Toyota" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Modèle</label>
                      <input value={form.modele} onChange={e => setForm({ ...form, modele: e.target.value })} placeholder="Ex: Corolla" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Année</label>
                      <input type="number" value={form.annee} onChange={e => setForm({ ...form, annee: e.target.value })} placeholder="Ex: 2022" style={inputStyle} />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Ville</label>
                      <select value={form.villeDepart} onChange={e => setForm({ ...form, villeDepart: e.target.value })} style={inputStyle}>
                        {VILLES.map(v => <option key={v}>{v}</option>)}
                      </select>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Capacité (places)</label>
                      <input type="number" value={form.capacite} onChange={e => setForm({ ...form, capacite: e.target.value })} placeholder="Ex: 5" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Disponible</label>
                      <select value={form.disponible ? "oui" : "non"} onChange={e => setForm({ ...form, disponible: e.target.value === "oui" })} style={inputStyle}>
                        <option value="oui">Oui — visible</option>
                        <option value="non">Non — masqué</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Prix / jour (F CFA) *</label>
                      <input type="number" value={form.prix} onChange={e => setForm({ ...form, prix: e.target.value })} placeholder="Ex: 30000" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Nom du propriétaire</label>
                      <input value={form.nomProprietaire} onChange={e => setForm({ ...form, nomProprietaire: e.target.value })} placeholder="Ex: Moussa Ba" style={inputStyle} />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Téléphone *</label>
                      <input value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} placeholder="+221 77 000 00 00" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>WhatsApp (numéro de contact direct)</label>
                      <input value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} placeholder="+221 77 000 00 00" style={inputStyle} />
                      <p style={{ fontSize: 11, color: "#B8A898", marginTop: 5 }}>Les clients intéressés cliqueront sur ce numéro pour discuter directement avec vous.</p>
                    </div>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={labelStyle}>Description</label>
                    <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                      placeholder="Décrivez le véhicule, ses options, conditions de location…"
                      rows={4} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={labelStyle}>Équipements</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {EQUIPEMENTS_LISTE.map(e => (
                        <span key={e} className="pill" onClick={() => toggleEquip(e)}
                          style={{ padding: "6px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500, border: `1.5px solid ${form.equipements.includes(e) ? "#C4441A" : "#E8DCC8"}`, background: form.equipements.includes(e) ? "rgba(196,68,26,0.08)" : "white", color: form.equipements.includes(e) ? "#C4441A" : "#7A5A3A" }}>
                          {form.equipements.includes(e) ? "✓ " : ""}{e}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: 24 }}>
                    <label style={labelStyle}>Photos du véhicule</label>
                    <div className="photo-zone" onClick={() => fileRef.current?.click()}>
                      <input ref={fileRef} type="file" multiple accept="image/*" onChange={e => uploadPhotos(Array.from(e.target.files))} style={{ display: "none" }} />
                      {uploading ? <p style={{ color: "#7A5A3A", margin: 0, fontSize: 14 }}>Chargement…</p> : <><IcUpload /><p style={{ color: "#7A5A3A", margin: "8px 0 4px", fontSize: 14, fontWeight: 600 }}>Cliquez pour ajouter des photos</p></>}
                    </div>
                    {photos.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
                        {photos.map((url, i) => (
                          <div key={i} style={{ position: "relative", width: 80, height: 80, borderRadius: 8, overflow: "hidden" }}>
                            <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            <button onClick={() => setPhotos(prev => prev.filter(p => p !== url))}
                              style={{ position: "absolute", top: 4, right: 4, background: "rgba(196,68,26,0.85)", border: "none", borderRadius: "50%", width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "white" }}>
                              <IcX />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button onClick={() => { setShowForm(false); setForm(VEHICULE_VIDE); setPhotos([]); setEditId(null); }}
                      style={{ background: "white", border: "1.5px solid #E8DCC8", borderRadius: 10, padding: "11px 20px", fontSize: 14, fontWeight: 600, color: "#7A5A3A", cursor: "pointer" }}>Annuler</button>
                    <button className="btn-primary" onClick={sauvegarderVehicule} disabled={saving}
                      style={{ color: "white", borderRadius: 10, padding: "11px 28px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 700, opacity: saving ? 0.7 : 1 }}>
                      {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                  </div>
                </div>
              )}

              {vehicules.length === 0 && !showForm ? (
                <div style={{ textAlign: "center", padding: 60, color: "#7A5A3A" }}>
                  <p style={{ fontSize: 15, marginBottom: 16 }}>Aucun véhicule enregistré.</p>
                  <button className="btn-primary" onClick={() => setShowForm(true)} style={{ color: "white", borderRadius: 10, padding: "12px 24px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 700 }}>
                    + Ajouter mon premier véhicule
                  </button>
                </div>
              ) : (
                <div className="veh-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
                  {vehicules.map(v => (
                    <div key={v.id} className="vehicule-card" style={{ background: "white", borderRadius: 14, border: "1.5px solid #E8DCC8", overflow: "hidden", boxShadow: "0 2px 12px rgba(28,16,8,0.06)" }}>
                      <div style={{ height: 130, background: "linear-gradient(135deg,#1C1008,#2D1A08)", overflow: "hidden", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {v.images?.[0] ? <img src={v.images[0]} alt={v.nom} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ color: "white" }}><IcCar /></div>}
                        <span style={{ position: "absolute", top: 10, right: 10, fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: v.disponible ? "rgba(61,107,46,0.85)" : "rgba(196,68,26,0.85)", color: "white" }}>
                          {v.disponible ? "Disponible" : "Masqué"}
                        </span>
                      </div>
                      <div style={{ padding: 16 }}>
                        <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 14, fontWeight: 800, color: "#1C1008", margin: "0 0 4px" }}>{v.nom}</h3>
                        <div style={{ fontSize: 12, color: "#7A5A3A", marginBottom: 10 }}>{v.typeVehicule || v.type} · {v.villeDepart} · {v.capacite} places</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: "1px solid #F2E4C8" }}>
                          <div>
                            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 16, fontWeight: 800, color: "#C4441A" }}>{formatPrix(v.prix)}</div>
                            <div style={{ fontSize: 11, color: "#7A5A3A" }}>par jour</div>
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={() => ouvrirEdit(v)} style={{ background: "rgba(196,68,26,0.08)", border: "none", borderRadius: 8, padding: "7px 10px", fontSize: 12, fontWeight: 600, color: "#C4441A", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                              <IcEdit />
                            </button>
                            <button onClick={() => supprimerVehicule(v.id)} style={{ background: "rgba(196,68,26,0.06)", border: "none", borderRadius: 8, padding: "7px 10px", fontSize: 12, fontWeight: 600, color: "#C4441A", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                              <IcTrash />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

        </div>
      </div>
      <Footer />
    </>
  );
}